<template>
  <modal
    name="create-temporary-agreement"
    class="modal-inner"
    transition="pop-out"
    :width="modalWidth"
    :focus-trap="true"
    height="auto"
    :scrollable="true"
    :adaptive="true"
    :reset="true"
    @before-open="beforeOpen"
  >
    <span class="close-button" @click="hide('create-temporary-agreement')">
      <BaseIcon icon="times-circle" class="text-white" />
    </span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t("create_temporary_agreement.title") }}
    </div>
    <div class="modal-body">
      <ValidationObserver ref="formCreateTemp">
        <form>
          <!-- Auto-generated agreement number (read-only) -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput
                v-model="agreement_number_preview"
                type="text"
                :field_name="$t('agreement_create.agreement_number')"
                readonly
                disabled
              />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- E-faktura -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="e_bill" type="text" :field_name="$t('agreement_create.e_invoice')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Hyrestagare -->
          <div class="flex flex-wrap">
            <div class="w-full text-sm font-bold italic px-2 mb-4">
              {{ $t("agreement_create.lessee") }}
            </div>

            <!-- Optional customer linking -->
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect
                v-model="agreement_type"
                :options="agreement_types"
                nameSelector="name"
                optionSelector="id"
                :field_name="$t('agreement_create.agreement_type')"
                @change="changeAgreementType()"
              />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2" v-if="agreement_type === 'customer'">
              <!-- Vald kund -->
              <div v-if="customer_id">
                <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('agreement_create.customer') }}</label>
                <div class="mt-2 flex items-center bg-green-50 border border-green-200 rounded px-3 py-2">
                  <span class="text-sm flex-1">{{ selectedCustomerName }}</span>
                  <button @click.prevent="clearCustomerSelection()" class="text-red-400 hover:text-red-600 ml-2 text-xs font-bold">✕</button>
                </div>
              </div>
              <!-- Sökfält -->
              <div v-else class="relative">
                <BaseInput
                  v-model="customerSearchQuery"
                  type="text"
                  :field_name="$t('agreement_create.customer')"
                  :placeholder="$t('create_temporary_agreement.customer_search_placeholder')"
                />
                <p v-if="customerSearchQuery.length > 0 && customerSearchQuery.length < 4" class="text-xs text-gray-400 mt-1">
                  {{ $t('create_temporary_agreement.search_min_chars') }}
                </p>
                <div v-if="customerSearchQuery.length >= 4" class="absolute z-50 w-full bg-white border border-gray-200 rounded shadow-lg max-h-52 overflow-y-auto mt-1">
                  <div v-if="filteredCustomers.length === 0" class="px-3 py-2 text-gray-500 text-sm">
                    {{ $t('create_temporary_agreement.no_results') }}
                  </div>
                  <div
                    v-for="c in filteredCustomers"
                    :key="c._id"
                    class="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-0"
                    @click="selectCustomer(c)"
                  >
                    {{ c.name }}
                  </div>
                </div>
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="agreement_type === 'customer' && customer_id !== ''">
              <BaseSelect
                v-model="unit_id"
                :options="[{ _id: '', name: $t('create_temporary_agreement.no_unit') }, ...customer_units]"
                nameSelector="name"
                optionSelector="_id"
                :field_name="$t('agreement_create.units')"
                @change="preFillUnitAddress()"
              />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="agreement_type === 'reseller'">
              <BaseSelect
                v-model="reseller_id"
                :options="[{ _id: '', name: $t('create_temporary_agreement.no_reseller') }, ...resellers]"
                nameSelector="name"
                optionSelector="_id"
                :field_name="$t('agreement_create.reseller')"
                @change="preFillResellerAddress()"
              />
            </div>
          </div>

          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="customer_name" type="text" :field_name="$t('agreement_create.name')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="company_number" type="text" :field_name="$t('agreement_create.org_number')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="customer_phone" type="text" :field_name="$t('agreement_create.phone')" />
            </div>
          </div>
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="billing_adress" type="text" :field_name="$t('agreement_create.invoice_address')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="billing_zip_code" type="text" :field_name="$t('agreement_create.zip_code')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="billing_city" type="text" :field_name="$t('agreement_create.city')" />
            </div>
          </div>
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="customer_contact" type="text" :field_name="$t('agreement_create.contact_person')" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="customer_reference" type="text" :field_name="$t('agreement_create.ref')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Hyresgivare (read-only) -->
          <div class="flex flex-wrap">
            <div class="w-full text-sm font-bold italic px-2 mb-4">
              {{ $t("agreement_create.lessor") }}
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="dealer_name" type="text" :field_name="$t('agreement_create.name') + ' '" readonly disabled />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="dealer_company_number" type="text" :field_name="$t('agreement_create.org_number') + ' '" readonly disabled />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="dealer_phone" type="text" :field_name="$t('agreement_create.phone') + ' '" readonly disabled />
            </div>
          </div>
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="dealer_address" type="text" :field_name="$t('agreement_create.address') + ' '" readonly disabled />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="dealer_zip_code" type="text" :field_name="$t('agreement_create.zip_code') + ' '" readonly disabled />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="dealer_city" type="text" :field_name="$t('agreement_create.city') + ' '" readonly disabled />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Säljare -->
          <div class="flex flex-wrap mt-4">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="seller" :options="sellers" nameSelector="name" optionSelector="_id" :field_name="$t('admin_order_alarm.seller')" rules="required" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Avtalskategori & Försäkring -->
          <div class="flex flex-wrap mt-4">
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="agreement_category" :options="agreementCategoryOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.agreement_category')" rules="required" @change="onAgreementCategoryChange()" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSwitch v-model="insurance" :default="true" :field_name="$t('admin_order_alarm.insurance')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Produkter (grupperade per kategori/subkategori) -->
          <div class="flex flex-wrap">
            <div class="w-full px-2">
              <div class="flex flex-col">
                <div class="w-full text-gray-600 text-sm font-bold pb-3 font-serif">
                  {{ $t('admin_order_alarm.product') }}
                </div>

                <div class="space-y-2">
                  <div v-for="category in categoriesWithProducts" :key="category.value" class="border border-gray-200 rounded">
                    <!-- Kategoriheader -->
                    <div
                      class="px-4 py-2 bg-gray-50 cursor-pointer flex justify-between items-center hover:bg-gray-100"
                      @click="toggleCategory(category.value)"
                    >
                      <div class="flex items-center">
                        <span class="text-sm font-semibold text-gray-800">{{ category.label }}</span>
                        <span class="ml-2 text-xs text-gray-500">({{ groupedProducts[category.value].length }})</span>
                      </div>
                      <span class="text-gray-500 text-xs">{{ expandedCategories[category.value] ? '▲' : '▼' }}</span>
                    </div>

                    <!-- Kategoriinnehåll -->
                    <div v-show="expandedCategories[category.value]" class="bg-white">

                      <!-- Med subkategorier -->
                      <div v-if="subcategoriesByParent[category.value] && subcategoriesByParent[category.value].length > 0">
                        <div v-for="subcategory in subcategoriesByParent[category.value]" :key="subcategory._id" class="border-b border-gray-100">
                          <div
                            class="px-4 py-2 pl-8 bg-blue-50 cursor-pointer flex justify-between items-center hover:bg-blue-100"
                            @click="toggleSubcategory(subcategory._id)"
                          >
                            <div class="flex items-center">
                              <span class="text-sm font-medium text-blue-800">{{ subcategory.displayName }}</span>
                              <span class="ml-2 text-xs text-blue-600">({{ getProductsForSubcategory(category.value, subcategory._id).length }})</span>
                            </div>
                            <span class="text-blue-500 text-xs">{{ expandedSubcategories[subcategory._id] ? '▲' : '▼' }}</span>
                          </div>
                          <div v-show="expandedSubcategories[subcategory._id]" class="pl-8 pr-2 py-2 space-y-1">
                            <template v-for="p in getProductsForSubcategory(category.value, subcategory._id)">
                              <div :key="p._id || p.type" class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                                <div class="w-2/5 text-sm flex items-center">
                                  <button v-if="p.variants && p.variants.length > 0" type="button" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                    {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                  </button>
                                  <div class="flex-1">
                                    <template v-if="p.display_name">
                                      <div class="font-semibold">{{ p.display_name }}</div>
                                      <div class="text-xs text-gray-500">{{ p.name }}</div>
                                    </template>
                                    <template v-else>
                                      <span class="font-medium">{{ p.name }}</span>
                                    </template>
                                    <div v-if="p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} {{ $t('products.variants') }}</div>
                                  </div>
                                </div>
                                <template v-if="p.variants && p.variants.length > 0">
                                  <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                                </template>
                                <template v-else>
                                  <div class="w-1/5 px-2"><input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)"><input type="number" v-model.number="p.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-else-if="shouldShowCashForProduct(p)"><input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-else></div>
                                </template>
                              </div>
                              <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                                <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                  <div class="w-2/5 text-sm pl-2 flex items-center">
                                    <span class="text-blue-500 mr-2">↳</span>
                                    <div>
                                      <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                      <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                    </div>
                                  </div>
                                  <div class="w-1/5 px-2"><input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)"><input type="number" v-model.number="variant.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="(variant.recommended_rental_price || p.recommended_rental_price) > 0 ? 'Rek: ' + (variant.recommended_rental_price || p.recommended_rental_price) : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-else-if="shouldShowCashForProduct(p)"><input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="(variant.recommended_cash_price || p.recommended_cash_price) > 0 ? 'Rek: ' + (variant.recommended_cash_price || p.recommended_cash_price) : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                                </div>
                              </template>
                            </template>
                          </div>
                        </div>
                        <!-- Produkter utan subkategori -->
                        <div v-if="getProductsWithoutSubcategory(category.value).length > 0" class="border-b border-gray-100">
                          <div
                            class="px-4 py-2 pl-8 bg-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-200"
                            @click="toggleSubcategory(`${category.value}-none`)"
                          >
                            <div class="flex items-center">
                              <span class="text-sm font-medium text-gray-600">{{ $t('products.without_subcategory') }}</span>
                              <span class="ml-2 text-xs text-gray-500">({{ getProductsWithoutSubcategory(category.value).length }})</span>
                            </div>
                            <span class="text-gray-500 text-xs">{{ expandedSubcategories[`${category.value}-none`] ? '▲' : '▼' }}</span>
                          </div>
                          <div v-show="expandedSubcategories[`${category.value}-none`]" class="pl-8 pr-2 py-2 space-y-1">
                            <template v-for="p in getProductsWithoutSubcategory(category.value)">
                              <div :key="p._id || p.type" class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                                <div class="w-2/5 text-sm flex items-center">
                                  <button v-if="p.variants && p.variants.length > 0" type="button" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                    {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                  </button>
                                  <div class="flex-1">
                                    <template v-if="p.display_name">
                                      <div class="font-semibold">{{ p.display_name }}</div>
                                      <div class="text-xs text-gray-500">{{ p.name }}</div>
                                    </template>
                                    <template v-else>
                                      <span class="font-medium">{{ p.name }}</span>
                                    </template>
                                    <div v-if="p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} {{ $t('products.variants') }}</div>
                                  </div>
                                </div>
                                <template v-if="p.variants && p.variants.length > 0">
                                  <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                                </template>
                                <template v-else>
                                  <div class="w-1/5 px-2"><input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)"><input type="number" v-model.number="p.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-else-if="shouldShowCashForProduct(p)"><input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-else></div>
                                </template>
                              </div>
                              <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                                <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                  <div class="w-2/5 text-sm pl-2 flex items-center">
                                    <span class="text-blue-500 mr-2">↳</span>
                                    <div>
                                      <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                      <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                    </div>
                                  </div>
                                  <div class="w-1/5 px-2"><input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)"><input type="number" v-model.number="variant.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="(variant.recommended_rental_price || p.recommended_rental_price) > 0 ? 'Rek: ' + (variant.recommended_rental_price || p.recommended_rental_price) : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                                  <div class="w-1/5 px-2" v-else-if="shouldShowCashForProduct(p)"><input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="(variant.recommended_cash_price || p.recommended_cash_price) > 0 ? 'Rek: ' + (variant.recommended_cash_price || p.recommended_cash_price) : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                                </div>
                              </template>
                            </template>
                          </div>
                        </div>
                      </div>

                      <!-- Utan subkategorier: visa produkter direkt -->
                      <div v-else class="px-4 py-2 space-y-1">
                        <template v-for="p in groupedProducts[category.value]">
                          <div :key="p._id || p.type" class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                            <div class="w-2/5 text-sm flex items-center">
                              <button v-if="p.variants && p.variants.length > 0" type="button" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                {{ expandedVariants[p._id] ? '▼' : '▶' }}
                              </button>
                              <div class="flex-1">
                                <template v-if="p.display_name">
                                  <div class="font-semibold">{{ p.display_name }}</div>
                                  <div class="text-xs text-gray-500">{{ p.name }}</div>
                                </template>
                                <template v-else>
                                  <span class="font-medium">{{ p.name }}</span>
                                </template>
                                <div v-if="p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} {{ $t('products.variants') }}</div>
                              </div>
                            </div>
                            <template v-if="p.variants && p.variants.length > 0">
                              <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                            </template>
                            <template v-else>
                              <div class="w-1/5 px-2"><input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                              <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)"><input type="number" v-model.number="p.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                              <div class="w-1/5 px-2" v-else-if="shouldShowCashForProduct(p)"><input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" /></div>
                              <div class="w-1/5 px-2" v-else></div>
                            </template>
                          </div>
                          <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                            <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                              <div class="w-2/5 text-sm pl-2 flex items-center">
                                <span class="text-blue-500 mr-2">↳</span>
                                <div>
                                  <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                  <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                </div>
                              </div>
                              <div class="w-1/5 px-2"><input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                              <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)"><input type="number" v-model.number="variant.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="(variant.recommended_rental_price || p.recommended_rental_price) > 0 ? 'Rek: ' + (variant.recommended_rental_price || p.recommended_rental_price) : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                              <div class="w-1/5 px-2" v-else-if="shouldShowCashForProduct(p)"><input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="(variant.recommended_cash_price || p.recommended_cash_price) > 0 ? 'Rek: ' + (variant.recommended_cash_price || p.recommended_cash_price) : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" /></div>
                            </div>
                          </template>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Post-Products: Totals, Avtalsperiod, Uppläggningsavgift, Betalningsvillkor, Avtalsstart, Särskilda villkor, Leveransperiod -->
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/3 px-2 py-2" v-if="agreement_category === 'cash_services'">
              <BaseInput v-model.number="totalCashPrice" type="text" :field_name="$t('admin_order_alarm.total_cash_price')" disabled />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="agreement_category === 'rental' || agreement_category === 'financed'">
              <BaseInput v-model.number="cashSumForRentalFinanced" type="text" :field_name="$t('admin_order_alarm.cash_sum')" disabled />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="contract_period" :options="contractPeriodOptions" nameSelector="name" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.contract_period')" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="isContractPeriodFreetext">
              <BaseInput v-model.number="contract_period_freetext" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.contract_period_freetext_label')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model.number="setup_cost" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.setup_fee')" :placeholder="String(totalAlarmDevices * 995)" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="showRentalPrice">
              <BaseInput v-model="rental_month_cost" type="text" :field_name="$t('agreement_create.rental_cost')" disabled readonly />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="payment_terms" :options="paymentTermsOptions" nameSelector="name" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.payment_terms')" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="isPaymentTermsFreetext">
              <BaseInput v-model="payment_terms_freetext" type="text" :field_name="$t('agreement_create.payment_terms_freetext_label')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <ValidationProvider :name="$t('admin_order_alarm.contract_start')" rules="required" v-slot="{ classes, errors }">
                <div class="input-validate" :class="classes">
                  <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("agreement_create.contract_start") }}<b class="text-red-500 ml-1">*</b></label>
                  <date-picker
                    :lang="datePickerLocale"
                    :key="date_picker_langKey"
                    class="mt-2"
                    v-model="contract_start"
                    value-type="format"
                    format="YYYY-MM-DD"
                    :editable="false"
                    :clearable="false"
                  />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
            <div class="w-full px-2 py-2">
              <BaseTextarea rows="4" v-model="special_terms" :field_name="$t('admin_order_alarm.special_terms')" rules="required" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Objektspecifikation -->
          <div class="flex flex-wrap">
            <div class="w-full px-2 py-2">
              <BaseTextarea rows="3" v-model="units_specification" :field_name="$t('agreement_create.object_specification')" />
            </div>
          </div>

          <div class="mx-2 border-b border-secondary-400 my-5"></div>

          <!-- Spara-knapp -->
          <div class="flex flex-wrap">
            <div class="w-full px-2 pb-4">
              <div class="clearfix">
                <button
                  class="btn-blue float-right text-lg py-2 px-5"
                  type="submit"
                  :disabled="isSaving"
                  @click.prevent="saveTemporaryAgreement()"
                >
                  <span v-if="isSaving">
                    <BaseIcon icon="spinner" class="ml-1 animate-spin" />
                  </span>
                  <span v-else>
                    {{ $t("create_temporary_agreement.save") }}
                    <BaseIcon icon="arrow-right" class="ml-2" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 1100;

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/pl";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import siteContext from "@/utils/siteContext";

export default {
  name: "CreateTemporaryAgreement",
  siteContext,
  components: { DatePicker },
  props: {
    sellers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isSaving: false,
      customers: [],
      resellers: [],
      units: [],
      customer_units: [],
      agreement_type: "customer",
      customer_id: "",
      unit_id: "",
      reseller_id: "",
      e_bill: "",
      customer_name: "",
      company_number: "",
      customer_phone: "",
      billing_adress: "",
      billing_zip_code: "",
      billing_city: "",
      customer_contact: "",
      customer_reference: "",
      dealer_name: "",
      dealer_company_number: "",
      dealer_phone: "",
      dealer_address: "",
      dealer_zip_code: "",
      dealer_city: "",
      seller: "",
      units_specification: "",
      contract_start: "",
      contract_period: 0,
      contract_period_freetext: "",
      setup_cost: "",
      rental_month_cost: "",
      payment_terms: "",
      payment_terms_freetext: "",
      special_terms: "",
      agreement_category: "rental",
      insurance: true,
      agreement_number_preview: "",
      date_picker_lang: this.$i18n.locale,
      date_picker_langKey: 0,
      modalWidth: MODAL_WIDTH,
      productsFromDB: [],
      subcategories: [],
      expandedCategories: {},
      expandedSubcategories: {},
      expandedVariants: {},
      customerSearchQuery: '',
      products: [],
    };
  },

  computed: {
    agreement_types() {
      return [
        { id: "customer", name: this.$t("agreement_create.customers") },
        { id: "reseller", name: this.$t("agreement_create.resellers") },
      ];
    },
    agreementCategoryOptions() {
      return [
        { value: "rental", name: this.$t("admin_order_alarm.category_rental") },
        { value: "cash_services", name: this.$t("admin_order_alarm.category_cash_services") },
        { value: "financed", name: this.$t("admin_order_alarm.category_financed") },
      ];
    },
    contractPeriodOptions() {
      const months = this.$t('admin_order_alarm.months');
      const options = [];
      for (let i = 0; i <= 120; i += 12) {
        options.push({ value: i, name: `${i} ${months}` });
      }
      options.push({ value: '__freetext__', name: this.$t('admin_order_alarm.contract_period_freetext') });
      return options;
    },
    isContractPeriodFreetext() {
      return this.contract_period === '__freetext__';
    },
    isPaymentTermsFreetext() {
      return this.payment_terms === '__fritekst__' || this.payment_terms === '__fritext__';
    },
    totalAlarmDevices() {
      let count = 0;
      for (const p of this.products) {
        const qty = p.qty || 0;
        if (qty === 0) continue;
        if (p.category === 'alarm') count += qty;
      }
      return count;
    },
    showRentalPrice() {
      return ["rental", "financed", "cash_services"].includes(this.agreement_category);
    },
    showCashPrice() {
      return this.agreement_category === "cash_services";
    },
    visibleProducts() {
      // All three remaining categories (rental, financed, cash_services) show all products
      return this.products;
    },
    hardwareProducts() {
      return this.visibleProducts.filter(
        (p) =>
          !p.is_software &&
          p.type !== "simcard" &&
          p.type !== "other_monthly" &&
          p.type !== "addon_monthly" &&
          p.type !== "other_cash" &&
          p.type !== "addon_cash"
      );
    },
    softwareProducts() {
      return this.visibleProducts.filter(
        (p) =>
          p.is_software === true ||
          p.type === "simcard" ||
          p.type === "other_monthly" ||
          p.type === "addon_monthly" ||
          p.type === "other_cash" ||
          p.type === "addon_cash"
      );
    },
    cashSumForRentalFinanced() {
      if (!["rental", "financed"].includes(this.agreement_category)) return 0;
      let total = 0;
      this.products.forEach(p => {
        if (p.type === "addon_cash" || p.type === "other_cash") {
          if ((p.qty || 0) > 0 && (p.cash_price || 0) > 0) total += p.qty * p.cash_price;
          (p.variants || []).forEach(v => {
            if ((v.qty || 0) > 0 && (v.cash_price || 0) > 0) total += v.qty * v.cash_price;
          });
        }
      });
      return total;
    },
    totalCashPrice() {
      let total = 0;
      this.products.forEach(p => {
        if (this.agreement_category === "cash_services") {
          if (p.type === "simcard" || p.type === "other_monthly" || p.type === "addon_monthly") return;
        }
        if ((p.qty || 0) > 0 && (p.cash_price || 0) > 0) total += p.qty * p.cash_price;
        (p.variants || []).forEach(v => {
          if ((v.qty || 0) > 0 && (v.cash_price || 0) > 0) total += v.qty * v.cash_price;
        });
      });
      return total;
    },
    paymentTermsOptions() {
      const isNorwegian = this.$options.siteContext.isNorwegianSite();
      if (isNorwegian) {
        return [
          { value: "Årlig", name: this.$t("agreement_create.payment_terms_yearly") },
          { value: "Halvårlig", name: this.$t("agreement_create.payment_terms_half_year") },
          { value: "Kvartalsvis", name: this.$t("agreement_create.payment_terms_quarterly") },
          { value: "Månedlig", name: this.$t("agreement_create.payment_terms_monthly") },
          { value: "__fritekst__", name: this.$t("agreement_create.payment_terms_freetext") },
        ];
      }
      return [
        { value: "Årsvis", name: this.$t("agreement_create.payment_terms_yearly") },
        { value: "Halv år", name: this.$t("agreement_create.payment_terms_half_year") },
        { value: "Kvartal", name: this.$t("agreement_create.payment_terms_quarterly") },
        { value: "Månad", name: this.$t("agreement_create.payment_terms_monthly") },
        { value: "__fritext__", name: this.$t("agreement_create.payment_terms_freetext") },
      ];
    },
    datePickerLocale() {
      return this.date_picker_lang === "no" ? "nb" : this.date_picker_lang;
    },
    categoryOptions() {
      return [
        { value: 'alarm', label: this.$t('sales_dashboard.cat_personal_alarm') },
        { value: 'tag', label: this.$t('sales_dashboard.cat_tags') },
        { value: 'display', label: this.$t('sales_dashboard.cat_display') },
        { value: 'ajax', label: this.$t('sales_dashboard.cat_perimeter') },
        { value: 'vehicle_tracker', label: this.$t('sales_dashboard.cat_vehicle_tracker') },
        { value: 'software', label: this.$t('sales_dashboard.cat_software') },
        { value: 'simcard', label: this.$t('sales_dashboard.cat_simcard') },
        { value: 'accessory', label: this.$t('sales_dashboard.cat_accessory') },
        { value: 'other', label: this.$t('sales_dashboard.cat_other') },
      ];
    },
    groupedProducts() {
      const grouped = {};
      this.products.forEach(product => {
        const key = product.category || 'other';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(product);
      });
      return grouped;
    },
    subcategoriesByParent() {
      const grouped = {};
      this.subcategories.forEach(sub => {
        if (!grouped[sub.parentCategory]) grouped[sub.parentCategory] = [];
        grouped[sub.parentCategory].push(sub);
      });
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      });
      return grouped;
    },
    categoriesWithProducts() {
      return this.categoryOptions.filter(cat =>
        this.groupedProducts[cat.value] && this.groupedProducts[cat.value].length > 0
      );
    },
    selectedCustomerName() {
      if (!this.customer_id) return '';
      const customer = this.customers.find(c => c._id === this.customer_id);
      return customer ? customer.name : '';
    },
    // customer_id of the chosen customer: the server numbers a Dutch customer's agreement
    // NL and marks it Dutch (securecloud-server utils/country-origin)
    selectedCustomerId() {
      if (this.agreement_type !== 'customer' || !this.customer_id) return undefined;
      const customer = this.customers.find(c => c._id === this.customer_id);
      return customer ? customer.customer_id : undefined;
    },
    filteredCustomers() {
      if (this.customerSearchQuery.length < 4) return [];
      const q = this.customerSearchQuery.toLowerCase();
      return this.customers.filter(c => c.name && c.name.toLowerCase().includes(q)).slice(0, 30);
    },
  },

  methods: {
    beforeOpen() {
      this.resetForm();
      this.loadPreviewAgreementNumber();
      this.initializeDealerInfo();
      this.getCustomers();
      this.getResellers();
      this.getUnits();
    },

    resetForm() {
      this.isSaving = false;
      this.agreement_type = "customer";
      this.customer_id = "";
      this.unit_id = "";
      this.reseller_id = "";
      this.e_bill = "";
      this.customer_name = "";
      this.company_number = "";
      this.customer_phone = "";
      this.billing_adress = "";
      this.billing_zip_code = "";
      this.billing_city = "";
      this.customer_contact = "";
      this.customer_reference = "";
      this.seller = "";
      this.units_specification = "";
      this.contract_start = "";
      this.contract_period = 0;
      this.contract_period_freetext = "";
      this.setup_cost = "";
      this.rental_month_cost = "";
      this.payment_terms = "";
      this.payment_terms_freetext = "";
      this.special_terms = "";
      this.agreement_category = "rental";
      this.insurance = true;
      this.agreement_number_preview = "";
      this.customerSearchQuery = '';
      this.expandedCategories = {};
      this.expandedSubcategories = {};
      this.expandedVariants = {};
      this.products = this.products.map((p) => ({
        ...p,
        qty: 0,
        price: null,
        cash_price: null,
        variants: (p.variants || []).map(v => ({ ...v, qty: 0, price: null, cash_price: null })),
      }));
    },

    async loadPreviewAgreementNumber() {
      const previewRequest = (this._previewRequest = (this._previewRequest || 0) + 1);
      try {
        const res = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/agreements/preview-number`, {
          params: { selected_customer_id: this.selectedCustomerId },
        });
        // a newer customer choice is already loading
        if (previewRequest !== this._previewRequest) return;
        if (res.data) {
          const cat = this.agreement_category || 'rental';
          this.agreement_number_preview = res.data[cat] || res.data.agreement_number;
          // Store all variants for when category changes
          this._previewNumbers = res.data;
        }
      } catch {
        this.agreement_number_preview = this.$t("create_temporary_agreement.auto_generated");
      }
    },

    updatePreviewNumber() {
      if (this._previewNumbers) {
        const cat = this.agreement_category || 'rental';
        this.agreement_number_preview = this._previewNumbers[cat] || this._previewNumbers.agreement_number;
      } else {
        this.loadPreviewAgreementNumber();
      }
    },

    initializeDealerInfo() {
      const contactInfo = this.$options.siteContext.getContactInfo();
      const addressParts = contactInfo.company_address.split("\n");

      this.dealer_name = contactInfo.company_name;
      this.dealer_company_number = contactInfo.company_number;
      // The agreement stores and prints the dealer's number in Swedish national
      // form ("08-520 276 42") - the same value the server writes as its own
      // default (units/controllers/unit_controller.js). International format
      // drops the trunk zero, so the +46 country code is swapped back for it.
      // The optional 0 in the pattern keeps this working whether the contact
      // info holds "+46 8-..." or the older "+46 08-...".
      // A Norwegian number is left in international form, as it was before.
      this.dealer_phone = contactInfo.phone.replace(/^\+46[\s-]*0?/, "0");
      this.dealer_address = addressParts[0] || "";

      if (addressParts.length > 1) {
        const secondLine = addressParts[1].trim();
        if (secondLine.includes(",")) {
          const parts = secondLine.split(",");
          this.dealer_address = (this.dealer_address + " " + parts[0]).trim();
          const zipCityParts = parts[1].trim().match(/^(\d+\s?\d*)\s+(.+)$/);
          if (zipCityParts) {
            this.dealer_zip_code = zipCityParts[1];
            this.dealer_city = zipCityParts[2];
          }
        } else {
          const zipCityParts = secondLine.match(/^(\d+\s?\d*)\s+(.+)$/);
          if (zipCityParts) {
            this.dealer_zip_code = zipCityParts[1];
            this.dealer_city = zipCityParts[2];
          }
        }
      }

      this.payment_terms = this.$options.siteContext.isNorwegianSite() ? "Årlig" : "Årsvis";
    },

    getCustomers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/customer`)
        .then((res) => { this.customers = res.data.data; })
        .catch(this.handleError);
    },

    getResellers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/reseller`)
        .then((res) => { this.resellers = res.data.data; })
        .catch(this.handleError);
    },

    getUnits() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/units`)
        .then((res) => { this.units = res.data.data; })
        .catch(this.handleError);
    },

    changeAgreementType() {
      this.customer_id = "";
      this.unit_id = "";
      this.reseller_id = "";
      this.resetAddress();
      this.loadPreviewAgreementNumber();
    },

    preFillCustomerAddress() {
      const customer = this.customers.find((c) => c._id === this.customer_id);
      if (customer) {
        this.resetAddress();
        this.customer_name = customer.name;
        this.company_number = customer.company_number;
        this.customer_phone = customer.phone;
        this.billing_adress = customer.billing_address;
        this.billing_zip_code = customer.billing_zip_code;
        this.billing_city = customer.billing_city;
        this.customer_contact = customer.contact_name;
        this.customer_reference = customer.billing_reference;
        this.e_bill = customer.billing_invoice;
        this.filterCustomerUnits();
      }
    },

    preFillUnitAddress() {
      const unit = this.units.find((u) => u._id === this.unit_id);
      const customer = this.customers.find((c) => c._id === this.customer_id);
      if (unit) {
        this.resetAddress(false);
        this.customer_name = customer ? `${customer.name} - ${unit.name}` : unit.name;
        this.company_number = unit.company_number;
        this.customer_phone = unit.contact_phone;
        this.billing_adress = unit.billing_address;
        this.billing_zip_code = unit.billing_zip_code;
        this.billing_city = unit.billing_city;
        this.customer_contact = unit.contact_name;
        this.customer_reference = unit.billing_reference;
        this.e_bill = unit.billing_invoice;
      } else if (customer) {
        // Unit deselected — revert to customer name only
        this.customer_name = customer.name;
      }
    },

    preFillResellerAddress() {
      const reseller = this.resellers.find((r) => r._id === this.reseller_id);
      if (reseller) {
        this.resetAddress();
        this.customer_name = reseller.name;
        this.company_number = reseller.company_number;
        this.customer_phone = reseller.phone;
        this.billing_adress = reseller.address;
        this.billing_zip_code = reseller.zip_code;
        this.billing_city = reseller.city;
        this.customer_contact = reseller.contact_person;
        this.customer_reference = reseller.reference;
        this.e_bill = reseller.invoice;
      }
    },

    resetAddress(resetName = true) {
      if (resetName) this.customer_name = "";
      this.company_number = "";
      this.customer_phone = "";
      this.billing_adress = "";
      this.billing_zip_code = "";
      this.billing_city = "";
      this.customer_contact = "";
      this.customer_reference = "";
      this.e_bill = "";
    },

    filterCustomerUnits() {
      if (this.customer_id) {
        this.unit_id = "";
        const units = this.units.filter((u) => u.parent_id === this.customer_id);
        this.customer_units = units;
      }
    },

    getPaymentTermsValue() {
      if (this.payment_terms === "__fritekst__" || this.payment_terms === "__fritext__") {
        return this.payment_terms_freetext;
      }
      return this.payment_terms;
    },

    getContractPeriodValue() {
      if (this.contract_period === '__freetext__') {
        return this.contract_period_freetext;
      }
      return this.contract_period;
    },

    onAgreementCategoryChange() {
      this.calculateTotalPrice();
      this.updatePreviewNumber();
    },

    calculateTotalPrice() {
      let total = 0;
      const addToTotal = (p, qty, price) => {
        if (this.agreement_category === "rental" || this.agreement_category === "financed") {
          if (p.type === "addon_cash" || p.type === "other_cash") return;
          total += qty * (price || 0);
        } else if (this.agreement_category === "cash_services") {
          if (p.type === "simcard" || p.type === "other_monthly" || p.type === "addon_monthly") {
            total += qty * (price || 0);
          } else if (!p.is_software) {
            return;
          } else {
            total += qty * (price || 0);
          }
        } else {
          total += qty * (price || 0);
        }
      };
      this.products.forEach((p) => {
        if ((p.qty || 0) > 0) addToTotal(p, p.qty, p.price);
        (p.variants || []).forEach(v => { if ((v.qty || 0) > 0) addToTotal(p, v.qty, v.price); });
      });
      this.rental_month_cost = total;
    },

    isNumber(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault();
      }
    },

    shouldShowRentalForProduct(product) {
      const category = this.agreement_category;
      if (category === "rental" || category === "financed") {
        if (product.type === "addon_cash" || product.type === "other_cash") return false;
        return true;
      }
      if (category === "cash_services") {
        if (product.is_software === true) return true;
        if (product.type === "simcard" || product.type === "other_monthly" || product.type === "addon_monthly") return true;
        return false;
      }
      return false;
    },

    shouldShowCashForProduct(product) {
      const category = this.agreement_category;
      if (category === "rental" || category === "financed") {
        return product.type === "addon_cash" || product.type === "other_cash";
      }
      if (category === "cash_services") {
        if (product.type === "addon_cash" || product.type === "other_cash") return true;
        if (product.is_software !== true && product.type !== "other_monthly" && product.type !== "addon_monthly" && product.type !== "simcard") return true;
        return false;
      }
      return false;
    },

    async saveTemporaryAgreement() {
      const isValid = await this.$refs.formCreateTemp.validate();
      if (!isValid) return;

      this.isSaving = true;
      try {
        let cash_sum = "";
        if (this.agreement_category === "rental" || this.agreement_category === "financed") {
          cash_sum = String(this.cashSumForRentalFinanced);
        } else if (this.agreement_category === "cash_services") {
          cash_sum = String(this.totalCashPrice);
        }

        const data = {
          is_temporary: true,
          agreement_type: this.agreement_type,
          agreement_category: this.agreement_category,
          insurance: this.insurance,
          e_bill: this.e_bill,
          customer_name: this.customer_name,
          company_number: this.company_number,
          customer_phone: this.customer_phone,
          customer_contact: this.customer_contact,
          customer_reference: this.customer_reference,
          billing_adress: this.billing_adress,
          billing_zip_code: this.billing_zip_code,
          billing_city: this.billing_city,
          dealer_name: this.dealer_name,
          dealer_company_number: this.dealer_company_number,
          dealer_phone: this.dealer_phone,
          dealer_address: this.dealer_address,
          dealer_zip_code: this.dealer_zip_code,
          dealer_city: this.dealer_city,
          seller: this.seller,
          units_specification: this.units_specification,
          contract_start: this.contract_start,
          contract_period: this.getContractPeriodValue(),
          setup_cost: this.setup_cost,
          rental_month_cost: this.rental_month_cost,
          cash_sum,
          payment_terms: this.getPaymentTermsValue(),
          special_terms: this.special_terms,
          products: this.formatProductsForAgreement(),
          country: this.$options.siteContext.getSiteContext(),
          selected_customer_id: this.selectedCustomerId,
        };

        await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/agreements`, data);
        this.handleSuccess(this.$t("create_temporary_agreement.save_success"));
        this.$emit("agreement-created");
        this.hide("create-temporary-agreement");
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSaving = false;
      }
    },

    async fetchProducts() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`);
        this.productsFromDB = response.data.data;
        this.products = this.productsFromDB.map(p => ({
          _id: p._id,
          display_name: p.display_name || null,
          name: p.name,
          type: p.type,
          articleNumber: p.articleNumber || '',
          category: p.category || 'other',
          subcategory: p.subcategory || null,
          hasVariants: p.hasVariants || false,
          variants: (p.variants || []).map(v => ({ ...v, qty: 0, price: null, cash_price: null })),
          qty: 0,
          price: null,
          cash_price: null,
          is_software: p.is_software || false,
          always_visible: p.always_visible || false,
          recommended_rental_price: p.recommended_rental_price || 0,
          recommended_cash_price: p.recommended_cash_price || 0,
        }));
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    },

    async fetchSubcategories() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/subcategories`);
        this.subcategories = response.data.data;
      } catch (error) {
        console.error('Failed to fetch subcategories:', error);
      }
    },

    getProductsForSubcategory(categoryValue, subcategoryId) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => {
        if (!p.subcategory) return false;
        const productSubcatId = typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory;
        return String(productSubcatId) === String(subcategoryId);
      });
    },

    getProductsWithoutSubcategory(categoryValue) {
      const hasSubs = this.subcategoriesByParent[categoryValue] && this.subcategoriesByParent[categoryValue].length > 0;
      if (!hasSubs) return [];
      return (this.groupedProducts[categoryValue] || []).filter(p => !p.subcategory);
    },

    toggleCategory(value) {
      this.$set(this.expandedCategories, value, !this.expandedCategories[value]);
    },

    toggleSubcategory(id) {
      this.$set(this.expandedSubcategories, id, !this.expandedSubcategories[id]);
    },

    toggleVariants(id) {
      this.$set(this.expandedVariants, id, !this.expandedVariants[id]);
    },

    selectCustomer(customer) {
      this.customer_id = customer._id;
      this.customerSearchQuery = '';
      this.preFillCustomerAddress();
      // a Dutch customer's agreement number starts with NL
      this.loadPreviewAgreementNumber();
    },

    clearCustomerSelection() {
      this.customer_id = '';
      this.unit_id = '';
      this.customer_units = [];
      this.customerSearchQuery = '';
      this.resetAddress();
      this.loadPreviewAgreementNumber();
    },

    formatProductsForAgreement() {
      const formatted = [];
      this.products.forEach(p => {
        const displayName = p.display_name || p.name;
        if ((p.qty || 0) > 0) {
          formatted.push({
            display_name: displayName,
            name: p.name,
            type: p.type,
            articleNumber: p.articleNumber || '',
            category: p.category || 'other',
            is_software: p.is_software || false,
            qty: p.qty,
            price: p.price || 0,
            cash_price: p.cash_price || 0,
          });
        }
        (p.variants || []).forEach(v => {
          if ((v.qty || 0) > 0) {
            formatted.push({
              display_name: `${displayName} - ${v.name}`,
              name: `${p.name} - ${v.name}`,
              type: p.type,
              articleNumber: v.articleNumber || '',
              category: p.category || 'other',
              is_software: p.is_software || false,
              qty: v.qty,
              price: v.price || 0,
              cash_price: v.cash_price || 0,
            });
          }
        });
      });
      return formatted;
    },

    hide(id) {
      this.$modal.hide(id);
    },
  },

  created() {
    this._previewNumbers = null;
    this._previewRequest = 0;
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
  },

  async mounted() {
    await Promise.all([this.fetchProducts(), this.fetchSubcategories()]);
  },

  watch: {
    "$i18n.locale"(newLocale) {
      this.date_picker_lang = newLocale;
      this.date_picker_langKey++;
    },
  },
};
</script>
